import {
  ChangeEvent,
  Children,
  createElement,
  isValidElement,
  useMemo,
} from "react";
import { Controller } from "react-hook-form";

import {
  InputGroup
} from "../../Inputs/InputGroup/InputGroup.page";
import {
  useDisabledField
} from "../HookForm/HookForm.context";
import {
  InputRadioGroupProps
} from "./InputRadioGroup.types";

export function InputRadioGroup({
  helperText,
  children,
  name,
  rule,
  form,
  ...props
}: InputRadioGroupProps) {

  const isDisabled = useDisabledField(name);
  const fieldDisabled = isDisabled || props.disabled;
  const errorMessage = rule?.required === true
    ? 'Field cannot be empty'
    : (typeof rule?.required === 'string' ? rule.required : undefined);

  const parseValue = (value?: string) => {
    if (value === 'true') {
      return true;
    }
    if (value === 'false') {
      return false;
    }
    return value;
  };

  const computedRules = useMemo(() => {
    if (!rule) return undefined;
    const { required, validate, ...rest } = rule;
    if (!required) {
      return { ...rest, ...(validate !== undefined && { validate }) };
    }

    const reqMsg = typeof required === 'string' ? required : 'Field cannot be empty';
    const isSelected = (val: any) =>
      (val !== undefined && val !== null && val !== '') || reqMsg;

    if (typeof validate === 'function') {
      return {
        ...rest,
        validate: (val: any, formValues: any) => {
          const reqCheck = isSelected(val);
          if (reqCheck !== true) return reqCheck;
          return validate(val, formValues);
        }
      };
    }

    if (typeof validate === 'object' && validate !== null) {
      return {
        ...rest,
        validate: {
          required: isSelected,
          ...validate
        }
      };
    }

    return {
      ...rest,
      validate: isSelected
    };
  }, [rule?.required, rule?.validate]);

  return (
    <Controller
      control={form.control}
      name={name}
      rules={computedRules}
      render={({ field, fieldState }) => (
        <InputGroup
          helperText={fieldState.invalid ? (fieldState.error?.message?.toString() || errorMessage) : helperText}
          required={!!rule?.required}
          error={fieldState.invalid}
          {...props}
        >
          {Children.map(children, (child) => {
            if (isValidElement(child)) {
              const optionDisabled = fieldDisabled || child.props.disabled;
              return createElement(child.type, {
                ...child.props,
                name: field.name,
                onChange: (event: ChangeEvent<HTMLInputElement>) => {
                  if (optionDisabled) {
                    return;
                  }
                  field.onChange(parseValue(event.target.value));
                },
                checked: String(field.value ?? '') === String(child.props.value),
                key: child.key,
                disabled: optionDisabled,
                onBlur: field.onBlur,
              });
            }
            return (child);
          })}
        </InputGroup>
      )}
    />
  );
}
